import React from "react";
import { cn } from "@/lib/utils";

interface ModernCardProps {
  className?: string;
  children: React.ReactNode;
  variant?: "default" | "gradient" | "glass" | "robotic";
  interactive?: boolean;
}

export function ModernCard({ 
  className, 
  children, 
  variant = "default", 
  interactive = false 
}: ModernCardProps) {
  const baseClasses = "relative rounded-2xl p-6 transition-all duration-300";
  
  const variants = {
    default: "bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700",
    gradient: "bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 border border-blue-200 dark:border-gray-600",
    glass: "bg-white/20 dark:bg-gray-900/20 backdrop-blur-lg border border-white/30 dark:border-gray-700/30",
    robotic: "bg-gradient-to-r from-slate-900 to-slate-800 border border-cyan-500/20 shadow-2xl shadow-cyan-500/10"
  };

  const interactiveClasses = interactive 
    ? "hover:scale-[1.02] hover:shadow-lg cursor-pointer" 
    : "";

  return (
    <div className={cn(baseClasses, variants[variant], interactiveClasses, className)}>
      {variant === "robotic" && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-cyan-500/5 to-blue-500/5 animate-pulse" />
      )}
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );
}

interface ModernCardHeaderProps {
  className?: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  action?: React.ReactNode;
}

export function ModernCardHeader({ className, children, icon, action }: ModernCardHeaderProps) {
  return (
    <div className={cn("flex items-center justify-between mb-4", className)}>
      <div className="flex items-center space-x-3">
        {icon && (
          <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 text-white">
            {icon}
          </div>
        )}
        <div className="text-lg font-semibold text-gray-900 dark:text-white">
          {children}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function ModernCardContent({ className, children }: { className?: string; children: React.ReactNode }) {
  return <div className={cn("space-y-4", className)}>{children}</div>;
}